import { RefreshCw } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '../../api/client';
import { useToast } from '../../components/ui/Toast';
import { DataGrid, type Column } from '../../components/ui/DataGrid';
import { Badge } from '../../components/ui/Badge';
import { Select } from '../../components/ui/Select';
import { Button } from '../../components/ui/Button';

interface AuditRow {
  id: string;
  actor_id: string | null;
  actor_role: string | null;
  actor_email: string | null;
  actor_seller_code?: string | null;
  actor_name?: string | null;
  action: string;
  entity: string;
  entity_id: string | null;
  before_json: unknown;
  after_json: unknown;
  field_changed?: string | null;
  old_value?: string | null;
  new_value?: string | null;
  created_at: string;
}

const ACTION_TONE: Record<string, 'default' | 'success' | 'destructive' | 'warning' | 'secondary'> =
  {
    CREATE: 'success',
    UPDATE: 'default',
    ROLE_CHANGE: 'warning',
    DEACTIVATE: 'destructive',
    REACTIVATE: 'success',
    PASSWORD_CHANGE: 'secondary',
    PASSWORD_RESET: 'secondary',
    APPROVE: 'success',
    REJECT: 'destructive',
  };

const fmtTime = (iso: string): string =>
  new Date(iso).toLocaleString(undefined, {
    year: '2-digit',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });

/** Compact "before → after" for the common {value} / {role} / {active} shapes; falls back to JSON. */
function renderChange(before: unknown, after: unknown): string {
  const pick = (o: unknown): string | null => {
    if (o == null || typeof o !== 'object') return null;
    const r = o as Record<string, unknown>;
    for (const k of ['value', 'role', 'active', 'mustChangePassword']) {
      if (k in r) return `${r[k]}`;
    }
    return JSON.stringify(r);
  };
  const b = pick(before);
  const a = pick(after);
  if (b != null && a != null) return `${b} → ${a}`;
  if (a != null) return a;
  if (b != null) return b;
  return '—';
}

interface Props {
  /** Deep-link entity filter (e.g. 'admin_settings' from the Settings screen). */
  defaultEntity?: string;
}

/**
 * Screen 44 — Audit log change history. Read-only view of the append-only audit_logs table:
 * who did what, when, with the before/after of each change. Filter by entity (users,
 * admin_settings, …) and action. Enforcement of visibility is server-side (AUDIT_VIEW).
 */
export default function AuditLog({ defaultEntity }: Props) {
  const { t } = useTranslation();
  const { showApiError } = useToast();
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [entity, setEntity] = useState(defaultEntity ?? '');
  const [action, setAction] = useState('');
  const [facets, setFacets] = useState<{ entities: string[]; actions: string[] }>({
    entities: [],
    actions: [],
  });

  const LIMIT = 200;

  const load = async (): Promise<void> => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: String(LIMIT) });
      if (entity) params.set('entity', entity);
      if (action) params.set('action', action);
      const { data } = await apiClient.get<{ data: AuditRow[]; total: number }>(
        `/admin/audit-logs?${params.toString()}`
      );
      setRows(data.data);
      setTotal(data.total);
    } catch (err) {
      showApiError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    apiClient
      .get<{ data: { entities: string[]; actions: string[] } }>('/admin/audit-logs/facets')
      .then(({ data }) => setFacets(data.data))
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    void load();
  }, [entity, action]); // eslint-disable-line react-hooks/exhaustive-deps

  const columns = useMemo<Column<AuditRow>[]>(
    () => [
      { key: 'created_at', header: t('admin.audit.when'), render: (r) => fmtTime(r.created_at) },
      {
        key: 'actor',
        header: t('admin.audit.actor'),
        render: (r) => (
          <div className="min-w-0">
            {/* The seller's code first (4.2): the same code printed on the sale's documents. */}
            <div className="truncate">
              {r.actor_seller_code && (
                <span className="mr-1.5 rounded bg-muted px-1.5 py-0.5 font-mono text-xs font-semibold">
                  {r.actor_seller_code}
                </span>
              )}
              {r.actor_name || r.actor_email || t('admin.audit.removedSystem')}
            </div>
            {r.actor_role && (
              <div className="text-xs text-muted-foreground">
                {t(`roles.${r.actor_role}`, { defaultValue: r.actor_role })}
              </div>
            )}
          </div>
        ),
      },
      {
        key: 'action',
        header: t('admin.audit.action'),
        render: (r) => <Badge variant={ACTION_TONE[r.action] ?? 'secondary'}>{r.action}</Badge>,
      },
      { key: 'entity', header: t('admin.audit.entity') },
      {
        key: 'entity_id',
        header: t('admin.audit.target'),
        render: (r) => <span className="font-mono text-xs">{r.entity_id ?? '—'}</span>,
      },
      {
        key: 'change',
        header: t('admin.audit.change'),
        noTruncate: true,
        render: (r) => (
          <span className="text-xs">
            {r.field_changed
              ? `${r.field_changed} : ${r.old_value ?? '—'} → ${r.new_value ?? '—'}`
              : renderChange(r.before_json, r.after_json)}
          </span>
        ),
      },
    ],
    [t]
  );

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1.5 text-sm">
          <span className="text-muted-foreground">{t('admin.audit.entity')}</span>
          <Select className="h-8 w-44" value={entity} onChange={(e) => setEntity(e.target.value)}>
            <option value="">{t('common.all')}</option>
            {facets.entities.map((en) => (
              <option key={en} value={en}>
                {en}
              </option>
            ))}
          </Select>
        </div>
        <div className="flex items-center gap-1.5 text-sm">
          <span className="text-muted-foreground">{t('admin.audit.action')}</span>
          <Select className="h-8 w-44" value={action} onChange={(e) => setAction(e.target.value)}>
            <option value="">{t('common.all')}</option>
            {facets.actions.map((ac) => (
              <option key={ac} value={ac}>
                {ac}
              </option>
            ))}
          </Select>
        </div>
        <Button size="sm" variant="outline" onClick={() => void load()}>
          <RefreshCw className="h-4 w-4" />
          {t('admin.audit.refresh')}
        </Button>
        <span className="text-xs text-muted-foreground">
          {total > rows.length
            ? t('admin.audit.showingNewest', { shown: rows.length, total })
            : t('admin.audit.entries', { count: total })}
        </span>
      </div>
      <DataGrid
        columns={columns}
        rows={rows}
        loading={loading}
        rowKey={(r) => r.id}
        pageSize={50}
        empty={t('admin.audit.empty')}
      />
    </div>
  );
}
